import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  Image,
  MenuProps,
  Pagination,
  Spin,
  Tooltip,
} from "antd";
import { MoreOutlined } from "@ant-design/icons";

import styles from "./ServiceCategoryCards.module.css";
import {
  ServiceCategory,
  sortServiceCategoriesByOrder,
} from "features/serviceCategory/model/serviceCategory";

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type Props = {
  data: ServiceCategory[];
  isFetching?: boolean;

  pagination?: PaginationState;
  onPageChange?: (page: number, pageSize: number) => void;

  onEdit?: (row: ServiceCategory) => void;
  onDelete?: (row: ServiceCategory) => void;

  onOpen?: (row: ServiceCategory) => void;
  onView?: (row: ServiceCategory) => void;
};

const clamp = (text?: string | null, max = 110) => {
  const s = (text ?? "").trim();
  if (!s) return "—";
  return s.length > max ? s.slice(0, max).trim() + "…" : s;
};

export const ServiceCategoryCards: React.FC<Props> = ({
  data,
  isFetching,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
  onOpen,
  onView,
}) => {
  const { t, i18n } = useTranslation();
  /** Порядок на странице задаётся полем `order` (по возрастанию). */
  const items = useMemo(() => sortServiceCategoriesByOrder(data ?? []), [data]);

  const getMenu = (row: ServiceCategory): MenuProps => ({
    items: [
      { key: "view", label: t("handbook.table.view", "Посмотреть") },
      { key: "edit", label: t("handbook.table.edit", "Редактировать") },
      {
        key: "delete",
        label: t("handbook.table.delete", "Удалить"),
        danger: true,
      },
    ],
    onClick: ({ key }) => {
      if (key === "view") onView?.(row) ?? onOpen?.(row);
      if (key === "edit") onEdit?.(row);
      if (key === "delete") onDelete?.(row);
    },
  });

  return (
    <div className={styles.wrapper}>
      {isFetching ? (
        <div className={styles.loading}>
          <Spin spinning />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          {t("handbook.table.nothingFound", "Ничего не найдено")}
        </div>
      ) : (
        <div className={styles.grid}>
          {items.map((row) => {
            const title = i18n.language?.startsWith("ky")
              ? row.nameKy
              : row.nameRu;
            const desc = i18n.language?.startsWith("ky")
              ? row.descriptionKy
              : row.descriptionRu;

            const thumb = row.photo?.filePathThumb || row.photo?.filePath || "";

            const isInactive = row.isActive === false;

            return (
              <div
                key={row.id ?? Math.random()}
                className={`${styles.card} ${isInactive ? styles.cardInactive : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => onOpen?.(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onOpen?.(row);
                }}
              >
                <div className={styles.header}>
                  <div className={styles.title} title={title}>
                    {title || "—"}
                  </div>

                  <div
                    className={styles.menuWrap}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      menu={getMenu(row)}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Tooltip title="Меню">
                        <Button
                          type="text"
                          className={styles.menuBtn}
                          icon={<MoreOutlined />}
                          aria-label="menu"
                        />
                      </Tooltip>
                    </Dropdown>
                  </div>
                </div>

                <div className={styles.imageWrap}>
                  {row.order != null && (
                    <div className={styles.orderBadge}>#{row.order}</div>
                  )}
                  {isInactive && (
                    <div className={styles.statusBadge}>
                      {t("common.inactive", "Неактивно")}
                    </div>
                  )}
                  {thumb ? (
                    <Image
                      src={thumb}
                      alt={title}
                      className={styles.image}
                      preview={false}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder}>No photo</div>
                  )}
                </div>

                <div className={styles.desc}>{clamp(desc, 140)}</div>
              </div>
            );
          })}
        </div>
      )}

      {pagination && (
        <div className={styles.pagination}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            responsive
            onChange={(page, size) => onPageChange?.(page, size)}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
          />
        </div>
      )}
    </div>
  );
};
